import React from "react";
import { REOPEN_WINDOW_HOURS } from "./supportEnums";
import { formatClock } from "./ticketRowMeta";

/** One cell of the three-up strip. */
const Cell = ({ label, value, tone = "default", last = false }) => (
  <div
    className={`flex-1 px-2.5 py-1.5 ${last ? "" : "border-r border-gray-200"} ${
      tone === "danger" ? "border-r-danger-bg bg-danger-bg" : ""
    }`}
  >
    <p
      className={`m-0 text-[9px] font-bold uppercase tracking-[0.08em] ${
        tone === "danger" ? "text-danger-dark" : "text-gray-400"
      }`}
    >
      {label}
    </p>
    <p
      className={`m-0 mt-0.5 font-mono text-sm font-bold ${
        tone === "danger" ? "text-danger-dark" : "text-gray-700"
      }`}
    >
      {value}
    </p>
  </div>
);

/** `5h 18m` / `18m` from a millisecond span. */
const formatSpan = (ms) => {
  if (ms == null || Number.isNaN(ms)) return "—";
  const minutes = Math.max(0, Math.floor(ms / 60000));
  const hours = Math.floor(minutes / 60);
  return hours > 0 ? `${hours}h ${minutes % 60}m` : `${minutes}m`;
};

/**
 * The ticket header's clock strip — the two SLA deadlines side by side, plus
 * age.
 *
 * Both clocks are shown at once on purpose: an agent needs to know whether the
 * pressure is "we never replied" or "we replied and went quiet", and those are
 * different failures with different fixes. Once resolved the strip switches to
 * the outcome — how late the first reply was, how long it took, what the
 * customer thought.
 *
 * @param {Object} props
 * @param {Object} props.ticket - Raw ticket detail (server field names).
 * @param {boolean} props.resolved
 * @param {number|null} [props.csat]
 * @param {number} [props.now]
 */
export function SlaStrip({ ticket, resolved, csat = null, now = Date.now() }) {
  const createdMs = ticket.created_at ? Date.parse(ticket.created_at) : null;
  const firstRepliedIso = ticket.first_responded_at || ticket.first_response_at;
  const firstDueIso = ticket.first_response_due_at;
  const firstBreached =
    Boolean(ticket.first_response_breached_at) ||
    (!firstRepliedIso && firstDueIso && Date.parse(firstDueIso) <= now);

  /** How the first-response clock ended up — the same fact before and after resolve. */
  const firstResponseValue = () => {
    if (firstRepliedIso && firstDueIso) {
      const delta = Date.parse(firstRepliedIso) - Date.parse(firstDueIso);
      return delta > 0 ? `+${formatClock(delta)} LATE` : `${formatSpan(
        createdMs ? Date.parse(firstRepliedIso) - createdMs : null
      )}`;
    }
    if (firstRepliedIso && createdMs) {
      return formatSpan(Date.parse(firstRepliedIso) - createdMs);
    }
    if (firstBreached && firstDueIso) {
      return `+${formatClock(now - Date.parse(firstDueIso))} OVER`;
    }
    if (firstDueIso) return `${formatClock(Date.parse(firstDueIso) - now)} left`;
    return "—";
  };

  if (resolved) {
    const resolvedMs = ticket.resolved_at ? Date.parse(ticket.resolved_at) : null;
    return (
      <div className="mt-2.5 flex items-stretch overflow-hidden rounded-md border border-gray-200">
        <Cell
          label="First response"
          value={firstResponseValue()}
          tone={firstBreached ? "danger" : "default"}
        />
        <Cell
          label="Time to resolve"
          value={
            createdMs && resolvedMs ? formatSpan(resolvedMs - createdMs) : "—"
          }
        />
        <Cell label="CSAT" value={csat == null ? "awaiting" : `★ ${csat} / 5`} last />
      </div>
    );
  }

  const nextDueIso = ticket.next_response_due_at;
  const nextBreached =
    Boolean(ticket.next_response_breached_at) ||
    (nextDueIso && Date.parse(nextDueIso) <= now);
  const paused = ticket.waiting_on === "customer";

  return (
    <div className="mt-2.5 flex items-stretch overflow-hidden rounded-md border border-gray-200">
      <Cell
        label="First response"
        value={firstResponseValue()}
        tone={firstBreached && !firstRepliedIso ? "danger" : "default"}
      />
      <Cell
        label="Next response"
        value={
          paused
            ? "PAUSED"
            : !firstRepliedIso
            ? "NOT STARTED"
            : nextBreached && nextDueIso
            ? `+${formatClock(now - Date.parse(nextDueIso))} OVER`
            : nextDueIso
            ? `${formatClock(Date.parse(nextDueIso) - now)} left`
            : "—"
        }
        tone={nextBreached && !paused ? "danger" : "default"}
      />
      <Cell
        label="Age · Reopens"
        value={`${formatSpan(createdMs ? now - createdMs : null)} · ${
          ticket.reopen_count || 0
        }`}
        last
      />
    </div>
  );
}

/**
 * How long the client can still reopen a resolved ticket, or null once the
 * window has closed.
 *
 * @param {string|null} resolvedAtIso
 * @param {number} [now]
 * @returns {string|null}
 */
export const reopenWindowLeft = (resolvedAtIso, now = Date.now()) => {
  if (!resolvedAtIso) return null;
  const closesAt = Date.parse(resolvedAtIso) + REOPEN_WINDOW_HOURS * 3600 * 1000;
  if (Number.isNaN(closesAt) || closesAt <= now) return null;
  const hours = Math.floor((closesAt - now) / 3600000);
  return hours >= 1 ? `${hours}h` : `${Math.floor((closesAt - now) / 60000)}m`;
};

export default SlaStrip;
